import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthService } from '../../public/auth.service';
import { ProfileService } from '../../profile/profile.service';
import { normalizeRoutePath } from './membership-access';
import { isCurrentPathAllowedForTrackerState, resolvePostLoginDestination } from './onboarding-routing';

/**
 * Enforces membership wall for authenticated users on main product routes.
 * Anonymous users pass through unchanged.
 */
@Injectable()
export class MembershipGuard implements CanActivate {
  constructor(
    private auth: AuthService,
    private profileService: ProfileService,
    private router: Router
  ) {}

  canActivate(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | boolean | UrlTree {
    if (!this.auth.hasToken()) {
      return true;
    }

    const path = normalizeRoutePath(state.url);
    const user = this.auth.user$.value || this._userFromStorage();
    if (!user) {
      return true;
    }

    if (this.needsEmailVerification(user)) {
      if (this.isEmailFlowPath(path)) {
        return true;
      }
      const hasEmail = !!(user?.email || user?.email_pending);
      return this.router.parseUrl(hasEmail ? '/verify-email' : '/add-email');
    }

    if (this.needsPhoneVerification(user)) {
      if (this.isPhoneFlowPath(path)) {
        return true;
      }
      const hasPhone = !!(user.phone_number || user.phone_number_pending);
      return this.router.parseUrl(hasPhone ? '/verify-phone-number' : '/add-phone');
    }

    return this.profileService.ensureProfileTrackerLoaded().pipe(
      map((tracker) => {
        if (isCurrentPathAllowedForTrackerState(state.url, user, tracker)) {
          return true;
        }
        const dest = resolvePostLoginDestination(user, tracker);
        if (!dest) {
          return true;
        }
        const tree = this.router.parseUrl(dest.path);
        if (dest.queryParams) {
          tree.queryParams = { ...tree.queryParams, ...dest.queryParams };
        }
        return tree;
      })
    );
  }

  private needsEmailVerification(user: any): boolean {
    if (!user) {
      return false;
    }
    if (this.hasInviteBypassEmailVerify(user)) {
      return false;
    }
    const hasEmail = !!(user.email || user.email_pending);
    if (!hasEmail) {
      return true;
    }
    return !user.is_email_verified;
  }

  private needsPhoneVerification(user: any): boolean {
    if (!user || this.hasInviteBypassPhoneVerify(user)) {
      return false;
    }
    const hasPhone = !!(user.phone_number || user.phone_number_pending);
    const isVerified = !!user.is_phone_number_verified;
    return !hasPhone || !isVerified;
  }

  private hasInviteBypassEmailVerify(user: any): boolean {
    return !!(user?.signup_invite_code?.bypass_email_verify);
  }

  private hasInviteBypassPhoneVerify(user: any): boolean {
    return !!(user?.signup_invite_code?.bypass_phone_verify);
  }

  private isEmailFlowPath(path: string): boolean {
    return ['/add-email', '/change-email', '/verify-email'].some(
      (p) => path === p || path.startsWith(p + '/')
    );
  }

  private isPhoneFlowPath(path: string): boolean {
    return ['/add-phone', '/change-phone', '/verify-phone-number'].some(
      (p) => path === p || path.startsWith(p + '/')
    );
  }

  /** Hydrate user for guard when async profile load has not completed yet. */
  private _userFromStorage(): any {
    try {
      const raw = localStorage.getItem('user');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }
}
